/**
 * Low-level HTTP transport.
 *
 * Responsibilities kept here, and nowhere else:
 *  - resolve the base URL from VITE_API_BASE_URL (default /api/v1)
 *  - send cookies (`credentials: 'include'`)
 *  - attach X-CSRF-Token on mutating requests
 *  - attach X-Tenant-Id on every request when a tenant is selected
 *  - normalise every failure into ApiError
 *
 * The tenant header is read once per request and the answer is discarded if the
 * active tenant changed while the request was in flight, so a response can never
 * cross from one tenant scope into another.
 *
 * Endpoint semantics live in client.ts so this file stays transport-only.
 */

import { ApiError, kindFromStatus } from './errors';
import { DecodeError, readProblemDetails, type Decoder } from './decode';

export const DEFAULT_API_BASE_URL = '/api/v1';

/** Headers the backend contract requires; named once to avoid typo drift. */
export const CSRF_HEADER = 'X-CSRF-Token';
export const TENANT_HEADER = 'X-Tenant-Id';
export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly signal?: AbortSignal;
  /** Set false only for endpoints that must run without tenant context. */
  readonly withTenant?: boolean;
  /** Set false for endpoints that must not treat 401 as a session failure. */
  readonly anonymous?: boolean;
}

export interface TransportContext {
  readonly baseUrl: string;
  /**
   * Reads the CSRF token at request time. Supplied as a reader rather than a copied
   * value so the transport can never hold a stale token: React runs child effects
   * before parent effects, so any "sync the copy" step would lag behind the first
   * request issued by a newly mounted page.
   */
  readonly getCsrfToken: () => string | null;
  /** Reads the active tenant at request time, for the same reason. */
  readonly getTenantId: () => string | null;
  /** Invoked when the API reports the session is no longer valid. */
  readonly onUnauthorized: () => void;
}

export function resolveBaseUrl(raw: string | undefined): string {
  const value = (raw ?? '').trim();
  if (value === '') return DEFAULT_API_BASE_URL;
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: RequestOptions['query'],
): { url: string; displayPath: string } {
  const search = new URLSearchParams();
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      search.set(key, String(value));
    }
  }
  const queryString = search.toString();
  const displayPath = `${baseUrl}${path}${queryString === '' ? '' : `?${queryString}`}`;
  return { url: displayPath, displayPath };
}

function isMutating(method: string): boolean {
  return method !== 'GET' && method !== 'HEAD';
}

const JSON_CONTENT_TYPE = 'application/json';

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;
  return parseBody(await response.text());
}

function parseBody(text: string): unknown {
  if (text.trim() === '') return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A non-JSON body (HTML error page, plain-text gateway error) is returned
    // verbatim so the error mapper can still include something useful.
    return text;
  }
}

function messageFromBody(body: unknown, fallback: string): string {
  const problem = readProblemDetails(body);
  if (problem.detail !== undefined && problem.detail !== '') return problem.detail;
  if (typeof body === 'string' && body.trim() !== '') {
    const condensed = body.trim().slice(0, 200);
    return condensed;
  }
  return fallback;
}

export class ApiTransport {
  private readonly context: TransportContext;

  constructor(context: TransportContext) {
    this.context = context;
  }

  get baseUrl(): string {
    return this.context.baseUrl;
  }

  async request<T>(path: string, decoder: Decoder<T>, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const { url, displayPath } = buildUrl(this.context.baseUrl, path, options.query);

    // Read session-dependent values now, not from a cached snapshot.
    const csrfToken = this.context.getCsrfToken();
    const tenantId = this.context.getTenantId();

    const headers = new Headers({ Accept: JSON_CONTENT_TYPE });

    if (options.body !== undefined) {
      headers.set('Content-Type', JSON_CONTENT_TYPE);
    }
    if (isMutating(method)) {
      // Double-submit CSRF: the token comes from login and is echoed on writes.
      if (csrfToken !== null) {
        headers.set(CSRF_HEADER, csrfToken);
      }
    }
    if (options.withTenant !== false && tenantId !== null) {
      headers.set(TENANT_HEADER, tenantId);
    }

    const init: RequestInit = {
      method,
      headers,
      // Session cookie is HttpOnly and must travel on every call.
      credentials: 'include',
    };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal !== undefined) init.signal = options.signal;

    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (cause) {
      // Aborts must stay aborts so TanStack Query can ignore them.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError({
        kind: 'network',
        message: `请求 ${method} ${displayPath} 失败：无法连接 API 服务。`,
        method,
        path: displayPath,
        cause,
      });
    }

    this.discardIfTenantChanged(tenantId, options);

    if (!response.ok) {
      throw this.failure(response.status, await readBody(response), method, displayPath, options);
    }

    return decodeBody(
      await readBody(response),
      decoder,
      path,
      method,
      displayPath,
      response.status,
    );
  }

  /**
   * POSTs a multipart form with upload progress (fetch cannot report it). The
   * browser sets the multipart Content-Type; headers, errors and the tenant-switch
   * discard follow `request`.
   */
  async upload<T>(
    path: string,
    form: FormData,
    decoder: Decoder<T>,
    onProgress?: (fraction: number) => void,
  ): Promise<T> {
    const method = 'POST';
    const { url, displayPath } = buildUrl(this.context.baseUrl, path, undefined);
    const csrfToken = this.context.getCsrfToken();
    const tenantId = this.context.getTenantId();

    const { status, text } = await new Promise<{ status: number; text: string }>(
      (resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Accept', JSON_CONTENT_TYPE);
        if (csrfToken !== null) xhr.setRequestHeader(CSRF_HEADER, csrfToken);
        if (tenantId !== null) xhr.setRequestHeader(TENANT_HEADER, tenantId);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
        };
        xhr.onload = () => {
          resolve({ status: xhr.status, text: xhr.responseText });
        };
        xhr.onerror = () => {
          reject(
            new ApiError({
              kind: 'network',
              message: `请求 ${method} ${displayPath} 失败：无法连接 API 服务。`,
              method,
              path: displayPath,
            }),
          );
        };
        xhr.onabort = () => {
          reject(new DOMException('upload aborted', 'AbortError'));
        };
        xhr.send(form);
      },
    );

    this.discardIfTenantChanged(tenantId, {});
    const body = status === 204 ? null : parseBody(text);
    if (status < 200 || status >= 300) {
      throw this.failure(status, body, method, displayPath, {});
    }
    return decodeBody(body, decoder, path, method, displayPath, status);
  }

  /** GETs a binary resource, such as an image that needs the tenant header. */
  async requestBlob(path: string, options: { readonly signal?: AbortSignal } = {}): Promise<Blob> {
    const method = 'GET';
    const { url, displayPath } = buildUrl(this.context.baseUrl, path, undefined);
    const tenantId = this.context.getTenantId();
    const headers = new Headers();
    if (tenantId !== null) headers.set(TENANT_HEADER, tenantId);
    const init: RequestInit = { method, headers, credentials: 'include' };
    if (options.signal !== undefined) init.signal = options.signal;

    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError({
        kind: 'network',
        message: `请求 ${method} ${displayPath} 失败：无法连接 API 服务。`,
        method,
        path: displayPath,
        cause,
      });
    }
    this.discardIfTenantChanged(tenantId, {});
    if (!response.ok) {
      throw this.failure(response.status, await readBody(response), method, displayPath, {});
    }
    return response.blob();
  }

  /**
   * A tenant switch while a request was in flight makes the answer belong to a
   * scope that is no longer selected. Returning it would let tenant A's rows be
   * written into tenant B's cache entry, so the response is discarded as an abort —
   * the same signal TanStack Query already ignores, which keeps a cancelled request
   * from being rendered as a failure.
   */
  private discardIfTenantChanged(
    tenantId: string | null,
    options: Pick<RequestOptions, 'withTenant'>,
  ): void {
    if (tenantId !== null && options.withTenant !== false) {
      if (this.context.getTenantId() !== tenantId) {
        throw new DOMException('tenant changed while the request was in flight', 'AbortError');
      }
    }
  }

  private failure(
    status: number,
    body: unknown,
    method: string,
    displayPath: string,
    options: Pick<RequestOptions, 'anonymous'>,
  ): ApiError {
    const problem = readProblemDetails(body);

    if (status === 401 && options.anonymous !== true) {
      this.context.onUnauthorized();
    }

    const init: ConstructorParameters<typeof ApiError>[0] = {
      kind: kindFromStatus(status),
      message: messageFromBody(body, `${method} ${displayPath} 返回 ${String(status)}`),
      method,
      path: displayPath,
      status,
      fieldIssues: problem.fieldIssues,
    };
    if (problem.code !== undefined) {
      Object.assign(init, { code: problem.code });
    }
    if (problem.requestId !== undefined) {
      Object.assign(init, { requestId: problem.requestId });
    }
    return new ApiError(init);
  }
}

function decodeBody<T>(
  body: unknown,
  decoder: Decoder<T>,
  path: string,
  method: string,
  displayPath: string,
  status: number,
): T {
  try {
    return decoder(body, path);
  } catch (cause) {
    if (cause instanceof DecodeError) {
      throw new ApiError({
        kind: 'invalid_response',
        message: `${method} ${displayPath} 的响应不符合约定：${cause.message}`,
        method,
        path: displayPath,
        status,
        cause,
      });
    }
    throw cause;
  }
}
